import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useBlocker } from 'react-router-dom';
import {
  CheckIcon, XMarkIcon as XIcon, MagnifyingGlassIcon as SearchIcon,
  DocumentDuplicateIcon, ChevronRightIcon, ChevronDownIcon, PlusIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Brand, BrandNetwork, GtPrefix } from '../types';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import StatusBanner, { Status } from '../components/StatusBanner';

const PAGE_SIZE = 50;

type Pending = { allowed: boolean; data_allowed: boolean; steering_weight: number | null };

const BrandNetworks: React.FC = () => {
  // Enforced again on the API; this only stops a readonly account from
  // staging edits it could never save.
  const { canEdit } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const brandId = Number(searchParams.get('brand')) || null;

  const [brands, setBrands] = useState<Brand[]>([]);
  // The whole list, ~531 rows / ~70 KB, fetched once per brand. Everything
  // below -- search, filters, pagination, selection -- is derived from it, so
  // typing costs no request and "select all matching" is an exact number
  // rather than a promise about rows nobody has seen.
  const [rows, setRows] = useState<BrandNetwork[]>([]);
  const [dirty, setDirty] = useState<Map<number, Pending>>(new Map());

  // GT prefixes, loaded per operator when its row is expanded. They belong to
  // the operator rather than to this brand, so unlike the flags above these
  // edits are written straight through instead of being staged for Save.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [prefixes, setPrefixes] = useState<Record<number, GtPrefix[]>>({});
  const [loadingPrefixes, setLoadingPrefixes] = useState<Set<number>>(new Set());
  const [gtForm, setGtForm] = useState<{
    operatorId: number; tadig: string; editing: GtPrefix | null;
    cc_ndc: string; gt_node: string; mgt: string;
  } | null>(null);
  const [gtSaving, setGtSaving] = useState(false);
  const [gtDelete, setGtDelete] = useState<{ prefix: GtPrefix; tadig: string } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ country: '', agreement_type: '', status: '' });
  const [page, setPage] = useState(1);

  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const [confirm, setConfirm] = useState<
    | { kind: 'copy' }
    | { kind: 'switch'; nextBrandId: number | null }
    | { kind: 'discard' }
    | null
  >(null);

  const headerCheckbox = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoadingBrands(true);
        setBrands(await apiService.getBrands());
      } catch (error) {
        setStatus({ kind: 'error', text: `Could not load brands: ${(error as Error).message}` });
      } finally {
        setLoadingBrands(false);
      }
    })();
  }, []);

  const loadRows = useCallback(async (id: number) => {
    try {
      setLoadingRows(true);
      setRows(await apiService.getBrandNetworks(id));
      setDirty(new Map());
      setSelected(new Set());
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not load networks: ${(error as Error).message}` });
    } finally {
      setLoadingRows(false);
    }
  }, []);

  useEffect(() => {
    if (brandId) loadRows(brandId);
    else setRows([]);
  }, [brandId, loadRows]);

  // A ?brand= pointing at nothing should say so rather than render an empty table.
  useEffect(() => {
    if (!loadingBrands && brandId && !brands.some((b) => b.id === brandId)) {
      setStatus({ kind: 'error', text: `Brand ${brandId} not found.` });
      setSearchParams({});
    }
  }, [loadingBrands, brandId, brands, setSearchParams]);

  // --- derived state ---

  const effective = useMemo(
    () => rows.map((r) => ({ ...r, ...(dirty.get(r.operator_id) ?? {}) })),
    [rows, dirty]
  );

  const countries = useMemo(
    () => Array.from(new Set(rows.map((r) => r.country))).sort(),
    [rows]
  );

  const agreementTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.roaming_agreement_type).filter(Boolean) as string[])).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return effective.filter((r) => {
      if (term) {
        const haystack = `${r.tadig_code} ${r.operator_name} ${r.mcc_mnc} ${r.country}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (filters.country && r.country !== filters.country) return false;
      if (filters.agreement_type && r.roaming_agreement_type !== filters.agreement_type) return false;
      if (filters.status === 'allowed' && !r.allowed) return false;
      if (filters.status === 'denied' && r.allowed) return false;
      if (filters.status === 'data' && !r.data_allowed) return false;
      if (filters.status === 'no-data' && r.data_allowed) return false;
      // Finding the networks with no GT at all is the usual reason to come
      // here looking for something to add.
      if (filters.status === 'no-gt' && r.gt_prefix_count > 0) return false;
      return true;
    });
  }, [effective, search, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  // Filtering to fewer pages than the current one would otherwise show nothing.
  useEffect(() => { setPage(1); }, [search, filters, brandId]);

  const summary = useMemo(() => ({
    allowed: effective.filter((r) => r.allowed).length,
    data: effective.filter((r) => r.data_allowed).length,
    total: effective.length,
  }), [effective]);

  /** The split of the country currently filtered to, if it runs one.
   *
   *  Weights are ratios, not percentages -- the backend divides by their sum --
   *  so a total other than 100 still works and is shown as a hint rather than
   *  an error. A country needs two weighted operators before anything is
   *  divided at all. */
  const countrySplit = useMemo(() => {
    if (!filters.country) return null;
    const weighted = effective.filter(
      (r) => r.country === filters.country && r.allowed && (r.steering_weight ?? 0) > 0
    );
    if (weighted.length === 0) return null;
    const total = weighted.reduce((sum, r) => sum + (r.steering_weight ?? 0), 0);
    return {
      total,
      active: weighted.length >= 2,
      rows: weighted.map((r) => ({
        tadig: r.tadig_code,
        weight: r.steering_weight ?? 0,
        share: total > 0 ? ((r.steering_weight ?? 0) * 100) / total : 0,
      })),
    };
  }, [effective, filters.country]);

  const pageAllSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r.operator_id));
  const pageSomeSelected = pageRows.some((r) => selected.has(r.operator_id));

  useEffect(() => {
    if (headerCheckbox.current) {
      headerCheckbox.current.indeterminate = pageSomeSelected && !pageAllSelected;
    }
  }, [pageSomeSelected, pageAllSelected]);

  // --- unsaved-changes guards ---

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty.size > 0 && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (dirty.size === 0) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty.size]);

  // --- editing ---

  /** Stage a change, dropping the entry when it matches the server again, so
   *  toggling something on and back off shrinks the unsaved count to zero
   *  instead of queueing a no-op write. */
  const stage = useCallback((ids: number[], patch: Partial<Pending>) => {
    if (!canEdit) return;
    setDirty((prev) => {
      const next = new Map(prev);
      for (const id of ids) {
        const original = rows.find((r) => r.operator_id === id);
        if (!original) continue;
        const current = next.get(id) ?? {
          allowed: original.allowed,
          data_allowed: original.data_allowed,
          steering_weight: original.steering_weight,
        };
        const updated = { ...current, ...patch };
        if (
          updated.allowed === original.allowed &&
          updated.data_allowed === original.data_allowed &&
          updated.steering_weight === original.steering_weight
        ) {
          next.delete(id);
        } else {
          next.set(id, updated);
        }
      }
      return next;
    });
  }, [rows, canEdit]);

  const toggleOne = (row: BrandNetwork & Pending, field: 'allowed' | 'data_allowed') =>
    stage([row.operator_id], { [field]: !row[field] } as Partial<Pending>);

  const bulk = (patch: Partial<Pending>) => stage(Array.from(selected), patch);

  const handleSave = async () => {
    if (!brandId || dirty.size === 0) return;
    try {
      setSaving(true);
      setStatus(null);
      const entries = Array.from(dirty.entries()).map(([operator_id, v]) => ({ operator_id, ...v }));
      const result = await apiService.updateBrandNetworks(brandId, entries);
      // Refetch rather than trust local state: one 70 KB request buys certainty.
      await loadRows(brandId);
      setStatus({ kind: 'ok', text: `Saved ${result.updated} networks.` });
    } catch (error) {
      // The write is transactional, so this sentence is literally true.
      setStatus({
        kind: 'error',
        text: `Saving failed: ${(error as Error).message}. Nothing was saved — your changes are still here.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyTemplate = async () => {
    if (!brandId) return;
    try {
      setCopying(true);
      setStatus(null);
      const result = await apiService.copyGlobalTemplateToBrand(brandId);
      setConfirm(null);
      await loadRows(brandId);
      setStatus({ kind: 'ok', text: `Copied the global template: ${result.updated} networks.` });
    } catch (error) {
      setStatus({ kind: 'error', text: `Copying the template failed: ${(error as Error).message}` });
    } finally {
      setCopying(false);
    }
  };

  const requestBrandSwitch = (nextId: number | null) => {
    if (dirty.size > 0) setConfirm({ kind: 'switch', nextBrandId: nextId });
    else applyBrandSwitch(nextId);
  };

  const applyBrandSwitch = (nextId: number | null) => {
    setDirty(new Map());
    setConfirm(null);
    setSearchParams(nextId ? { brand: String(nextId) } : {});
  };

  const selectedBrand = brands.find((b) => b.id === brandId) || null;

  const pill = (on: boolean, onLabel: string, offLabel: string) => (
    <>
      {on ? <CheckIcon className="mr-1 h-3 w-3" /> : <XIcon className="mr-1 h-3 w-3" />}
      {on ? onLabel : offLabel}
    </>
  );
  // ---- GT prefixes -------------------------------------------------------
  // These edit the operator catalogue, not this brand: a GT identifies the
  // visited VLR, so it is the same network whoever is roaming on it. The panel
  // says so, and writes land immediately rather than joining `dirty` -- two
  // save models on one screen would be worse than one surprise.

  const loadPrefixes = useCallback(async (operatorId: number) => {
    setLoadingPrefixes((prev) => new Set(prev).add(operatorId));
    try {
      const list = await apiService.getGtPrefixes(operatorId);
      setPrefixes((prev) => ({ ...prev, [operatorId]: list }));
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not load GT prefixes: ${(error as Error).message}` });
    } finally {
      setLoadingPrefixes((prev) => {
        const next = new Set(prev);
        next.delete(operatorId);
        return next;
      });
    }
  }, []);

  const toggleExpanded = (operatorId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(operatorId)) {
        next.delete(operatorId);
      } else {
        next.add(operatorId);
        if (!prefixes[operatorId]) loadPrefixes(operatorId);
      }
      return next;
    });
  };

  /** Keep the row's count in step without refetching all 531 rows. */
  const setPrefixCount = (operatorId: number, count: number) =>
    setRows((prev) => prev.map((r) =>
      r.operator_id === operatorId ? { ...r, gt_prefix_count: count } : r
    ));

  const handleGtSave = async () => {
    if (!gtForm) return;
    const cc_ndc = gtForm.cc_ndc.trim();
    if (!cc_ndc) {
      setStatus({ kind: 'error', text: 'CC+NDC is required.' });
      return;
    }
    try {
      setGtSaving(true);
      setStatus(null);
      // Blank optional fields are sent as null, not "", so an unset GT node
      // reads as absent rather than as an empty string.
      const payload = {
        cc_ndc,
        gt_node: gtForm.gt_node.trim() || undefined,
        mgt: gtForm.mgt.trim() || undefined,
      };
      if (gtForm.editing) {
        await apiService.updateGtPrefix(gtForm.editing.id, payload);
      } else {
        await apiService.addGtPrefix(gtForm.operatorId, payload);
      }
      const list = await apiService.getGtPrefixes(gtForm.operatorId);
      setPrefixes((prev) => ({ ...prev, [gtForm.operatorId]: list }));
      setPrefixCount(gtForm.operatorId, list.length);
      setStatus({
        kind: 'ok',
        text: `${gtForm.editing ? 'Updated' : 'Added'} GT prefix ${cc_ndc} on ${gtForm.tadig}. It applies to every brand.`,
      });
      setGtForm(null);
    } catch (error) {
      setStatus({ kind: 'error', text: `Saving the GT prefix failed: ${(error as Error).message}` });
    } finally {
      setGtSaving(false);
    }
  };

  const handleGtDelete = async () => {
    if (!gtDelete) return;
    const { prefix, tadig } = gtDelete;
    try {
      setGtSaving(true);
      setStatus(null);
      await apiService.deleteGtPrefix(prefix.id);
      const list = await apiService.getGtPrefixes(prefix.operator_id);
      setPrefixes((prev) => ({ ...prev, [prefix.operator_id]: list }));
      setPrefixCount(prefix.operator_id, list.length);
      setStatus({ kind: 'ok', text: `Deleted GT prefix ${prefix.cc_ndc} from ${tadig}.` });
    } catch (error) {
      setStatus({ kind: 'error', text: `Deleting the GT prefix failed: ${(error as Error).message}` });
    } finally {
      setGtSaving(false);
      setGtDelete(null);
    }
  };

  const pillClass = (on: boolean) =>
    `inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
      on ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'
    } ${canEdit ? (on ? 'hover:bg-green-200' : 'hover:bg-red-200') : 'cursor-default'}`;

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Brand networks</h1>
          <p className="mt-2 text-sm text-gray-700">
            Which visited networks a brand's subscribers may register on. This list, not the global
            operator flags, decides every steering answer.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {canEdit && <button
            type="button"
            onClick={() => setConfirm({ kind: 'copy' })}
            disabled={!brandId || copying}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentDuplicateIcon className="mr-2 h-4 w-4" />
            {copying ? 'Copying...' : 'Copy global template'}
          </button>}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <select
          value={brandId ?? ''}
          onChange={(e) => requestBrandSwitch(Number(e.target.value) || null)}
          disabled={loadingBrands}
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-64 sm:text-sm border-gray-300 rounded-md"
        >
          <option value="">{loadingBrands ? 'Loading brands…' : 'Select a brand…'}</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>{b.name}{b.is_active ? '' : ' (inactive)'}</option>
          ))}
        </select>
        {brandId && !loadingRows && (
          <span className="text-sm text-gray-500">
            {summary.allowed} allowed · {summary.data} data · of {summary.total}
          </span>
        )}
      </div>

      {countrySplit && (
        <div
          className={`mt-4 rounded-md border-l-4 px-3 py-2 text-sm ${
            countrySplit.active
              ? 'border-indigo-400 bg-indigo-50 text-indigo-900'
              : 'border-amber-400 bg-amber-50 text-amber-900'
          }`}
        >
          <span className="font-medium">{filters.country}:</span>{' '}
          {countrySplit.rows.map((r, i) => (
            <span key={r.tadig}>
              {i > 0 && ' + '}
              {r.tadig} {r.weight}
            </span>
          ))}
          {' = '}
          {countrySplit.total}
          {countrySplit.active ? (
            <>
              {' → '}
              {countrySplit.rows.map((r, i) => (
                <span key={r.tadig} className="tabular-nums">
                  {i > 0 && ', '}
                  {r.tadig} {r.share.toFixed(1)}%
                </span>
              ))}
              {countrySplit.total !== 100 && (
                <span className="ml-2 opacity-80">
                  (shares are ratios, so this works — but a total of 100 is easier to read)
                </span>
              )}
            </>
          ) : (
            <span className="ml-2">
              — only one operator carries a share, so nothing is divided. A split needs two.
            </span>
          )}
        </div>
      )}

      <StatusBanner status={status} />

      {!brandId ? (
        <div className="mt-8 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <p className="text-sm text-gray-500">Select a brand to edit its network allow list.</p>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search TADIG, operator, MCC+MNC, country..."
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            <select
              value={filters.country}
              onChange={(e) => setFilters({ ...filters, country: e.target.value })}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option value="">All countries</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filters.agreement_type}
              onChange={(e) => setFilters({ ...filters, agreement_type: e.target.value })}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option value="">All agreement types</option>
              {agreementTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option value="">Any status</option>
              <option value="allowed">Allowed</option>
              <option value="denied">Denied</option>
              <option value="data">Data allowed</option>
              <option value="no-data">Data denied</option>
              <option value="no-gt">No GT prefixes</option>
            </select>
          </div>

          {selected.size > 0 && (
            <div className="mt-4 sticky top-0 z-10 flex items-center gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2">
              <span className="text-sm font-medium text-indigo-900">{selected.size} selected</span>
              <button onClick={() => bulk({ allowed: true })} className="text-sm text-indigo-700 underline hover:text-indigo-900">Allow</button>
              <button onClick={() => bulk({ allowed: false })} className="text-sm text-indigo-700 underline hover:text-indigo-900">Deny</button>
              <span className="text-indigo-300">|</span>
              <button onClick={() => bulk({ data_allowed: true })} className="text-sm text-indigo-700 underline hover:text-indigo-900">Data on</button>
              <button onClick={() => bulk({ data_allowed: false })} className="text-sm text-indigo-700 underline hover:text-indigo-900">Data off</button>
              <button onClick={() => setSelected(new Set())} className="ml-auto text-sm text-indigo-700 underline hover:text-indigo-900">Clear selection</button>
            </div>
          )}

          <div className="mt-6 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3.5 text-left">
                          <input
                            ref={headerCheckbox}
                            type="checkbox"
                            checked={pageAllSelected}
                            onChange={(e) => {
                              const next = new Set(selected);
                              pageRows.forEach((r) => e.target.checked ? next.add(r.operator_id) : next.delete(r.operator_id));
                              setSelected(next);
                            }}
                            aria-label="Select all networks on this page"
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">TADIG</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Country</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Operator</th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">MCC+MNC</th>
                        <th
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          title="Decides HLR queries — 2G/3G attach, voice and SMS"
                        >
                          Allowed <span className="font-normal text-gray-500">(HLR)</span>
                        </th>
                        <th
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          title="Decides HSS queries — on 4G/5G the attach is the data service"
                        >
                          Data <span className="font-normal text-gray-500">(HSS)</span>
                        </th>
                        <th
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          title="Relative share of subscribers steered onto this network, among the allowed operators of the same country. Blank means no split; two operators must carry a share before anything is divided."
                        >
                          Share
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {loadingRows ? (
                        <tr><td colSpan={8} className="text-center py-4">Loading networks...</td></tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-6 text-sm text-gray-500">
                            No networks match these filters.{' '}
                            <button
                              onClick={() => { setSearch(''); setFilters({ country: '', agreement_type: '', status: '' }); }}
                              className="text-indigo-600 hover:text-indigo-900 underline"
                            >
                              Clear filters
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <>
                          {pageAllSelected && filtered.length > pageRows.length && (
                            <tr className="bg-indigo-50">
                              <td colSpan={8} className="px-3 py-2 text-center text-sm text-indigo-800">
                                All {pageRows.length} networks on this page are selected.{' '}
                                <button
                                  onClick={() => setSelected(new Set(filtered.map((r) => r.operator_id)))}
                                  className="font-medium underline hover:text-indigo-900"
                                >
                                  Select all {filtered.length} matching networks
                                </button>
                              </td>
                            </tr>
                          )}
                          {pageRows.map((row) => {
                            const isDirty = dirty.has(row.operator_id);
                            const isOpen = expanded.has(row.operator_id);
                            return (
                              <React.Fragment key={row.operator_id}>
                              <tr className={isDirty ? 'bg-indigo-50' : undefined}>
                                <td className={`px-3 py-4 border-l-4 ${isDirty ? 'border-indigo-500' : 'border-transparent'}`}>
                                  <input
                                    type="checkbox"
                                    checked={selected.has(row.operator_id)}
                                    onChange={(e) => {
                                      const next = new Set(selected);
                                      e.target.checked ? next.add(row.operator_id) : next.delete(row.operator_id);
                                      setSelected(next);
                                    }}
                                    aria-label={`Select ${row.tadig_code}`}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                  />
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                                  <div className="flex items-center">
                                    <button
                                      onClick={() => toggleExpanded(row.operator_id)}
                                      aria-label={`${isOpen ? 'Hide' : 'Show'} GT prefixes for ${row.tadig_code}`}
                                      aria-expanded={isOpen}
                                      className="mr-2 rounded p-1 hover:bg-gray-100"
                                    >
                                      {isOpen
                                        ? <ChevronDownIcon className="h-4 w-4" />
                                        : <ChevronRightIcon className="h-4 w-4" />}
                                    </button>
                                    {row.tadig_code}
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{row.country}</td>
                                <td className="px-3 py-4 text-sm text-gray-500">{row.operator_name}</td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                                  {row.mcc_mnc}
                                  <button
                                    onClick={() => toggleExpanded(row.operator_id)}
                                    title="GT prefixes in the shared operator catalogue"
                                    className={`mt-0.5 block font-sans text-xs underline ${
                                      row.gt_prefix_count === 0
                                        ? 'text-amber-700 hover:text-amber-900'
                                        : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                  >
                                    {row.gt_prefix_count === 0
                                      ? 'no GT — HSS only'
                                      : `${row.gt_prefix_count} GT`}
                                  </button>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                  <button
                                    onClick={() => toggleOne(row, 'allowed')}
                                    aria-label={`Toggle roaming for ${row.tadig_code}`}
                                    className={pillClass(row.allowed)}
                                  >
                                    {pill(row.allowed, 'Allowed', 'Denied')}
                                  </button>
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                  <button
                                    onClick={() => toggleOne(row, 'data_allowed')}
                                    aria-label={`Toggle data roaming for ${row.tadig_code}`}
                                    title="Data roaming (3G)"
                                    className={pillClass(row.data_allowed)}
                                  >
                                    {pill(row.data_allowed, 'Data', 'No data')}
                                  </button>
                                </td>
                              
                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                  <input
                                    type="number"
                                    min={1}
                                    max={10000}
                                    value={row.steering_weight ?? ''}
                                    disabled={!canEdit || !row.allowed}
                                    title={row.allowed ? undefined : 'Only an allowed network can take a share'}
                                    placeholder="—"
                                    onChange={(e) => {
                                      const raw = e.target.value.trim();
                                      // Empty clears the weight, which takes the
                                      // network out of the split rather than
                                      // giving it a zero share.
                                      const value = raw === '' ? null
                                        : Math.min(10000, Math.max(1, Number(raw) || 1));
                                      stage([row.operator_id], { steering_weight: value });
                                    }}
                                    className="w-20 rounded-md border-gray-300 text-sm tabular-nums disabled:bg-gray-50 disabled:text-gray-400"
                                  />
                                </td>
                              </tr>
                              {isOpen && (
                                <tr className="bg-gray-50">
                                  <td colSpan={8} className="px-6 py-4">
                                    <div className="mb-3 flex items-center justify-between">
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-900">
                                          GT prefixes — {row.tadig_code}
                                        </h4>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                          A GT identifies the visited VLR, so these belong to the
                                          operator and <strong>apply to every brand</strong>. Edits here
                                          save immediately — they are not part of this page&rsquo;s Save.
                                        </p>
                                      </div>
                                      {canEdit && (
                                        <button
                                          onClick={() => setGtForm({
                                            operatorId: row.operator_id, tadig: row.tadig_code,
                                            editing: null, cc_ndc: '', gt_node: '', mgt: '',
                                          })}
                                          className="inline-flex flex-none items-center rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                        >
                                          <PlusIcon className="mr-1 h-3 w-3" />
                                          Add GT prefix
                                        </button>
                                      )}
                                    </div>

                                    {loadingPrefixes.has(row.operator_id) ? (
                                      <p className="py-2 text-sm text-gray-500">Loading GT prefixes…</p>
                                    ) : (prefixes[row.operator_id]?.length ?? 0) === 0 ? (
                                      <p className="py-2 text-sm text-gray-500">
                                        No GT prefixes. An HLR query can never match this network —
                                        it is reachable over HSS only.
                                      </p>
                                    ) : (
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead>
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">CC+NDC</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">GT node</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">MGT</th>
                                            <th className="px-3 py-2" />
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {prefixes[row.operator_id].map((prefix) => (
                                            <tr key={prefix.id}>
                                              <td className="whitespace-nowrap px-3 py-2 font-mono text-sm text-gray-900">{prefix.cc_ndc}</td>
                                              <td className="whitespace-nowrap px-3 py-2 font-mono text-sm text-gray-500">{prefix.gt_node || '—'}</td>
                                              <td className="whitespace-nowrap px-3 py-2 font-mono text-sm text-gray-500">{prefix.mgt || '—'}</td>
                                              <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                                                {canEdit && (
                                                  <>
                                                    <button
                                                      onClick={() => setGtForm({
                                                        operatorId: row.operator_id, tadig: row.tadig_code,
                                                        editing: prefix, cc_ndc: prefix.cc_ndc,
                                                        gt_node: prefix.gt_node ?? '', mgt: prefix.mgt ?? '',
                                                      })}
                                                      className="text-indigo-600 underline hover:text-indigo-900"
                                                    >
                                                      Edit
                                                    </button>
                                                    <button
                                                      onClick={() => setGtDelete({ prefix, tadig: row.tadig_code })}
                                                      className="ml-3 text-red-600 underline hover:text-red-900"
                                                    >
                                                      Delete
                                                    </button>
                                                  </>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {filtered.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of{' '}
                {filtered.length}
                {filtered.length !== rows.length && ` (${rows.length} total)`}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}

          {dirty.size > 0 && (
            <div className="sticky bottom-0 mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 shadow-lg">
              <span className="text-sm text-gray-700">
                {dirty.size} unsaved change{dirty.size === 1 ? '' : 's'}
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirm({ kind: 'discard' })}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirm?.kind === 'copy'}
        title="Copy global template"
        destructive
        busy={copying}
        confirmLabel="Copy"
        message={
          <>
            Replace all {rows.length} entries in <strong>{selectedBrand?.name}</strong>'s allow list
            with the global operator settings?
            {dirty.size > 0 && <> Your {dirty.size} unsaved change{dirty.size === 1 ? '' : 's'} will be discarded.</>}
            {' '}This cannot be undone.
          </>
        }
        onConfirm={handleCopyTemplate}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'switch'}
        title="Discard unsaved changes?"
        confirmLabel="Discard and switch"
        message={<>You have {dirty.size} unsaved change{dirty.size === 1 ? '' : 's'}. Switching brands will lose them.</>}
        onConfirm={() => confirm?.kind === 'switch' && applyBrandSwitch(confirm.nextBrandId)}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'discard'}
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        destructive
        message={<>Throw away {dirty.size} unsaved change{dirty.size === 1 ? '' : 's'}?</>}
        onConfirm={() => { setDirty(new Map()); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={blocker.state === 'blocked'}
        title="Discard unsaved changes?"
        confirmLabel="Leave"
        destructive
        message={<>You have {dirty.size} unsaved change{dirty.size === 1 ? '' : 's'} that will be lost.</>}
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />

      <Modal
        open={gtForm !== null}
        onClose={() => setGtForm(null)}
        title={gtForm?.editing ? `Edit GT prefix — ${gtForm.tadig}` : `Add GT prefix — ${gtForm?.tadig ?? ''}`}
        footer={
          <>
            <button
              type="button"
              onClick={handleGtSave}
              disabled={gtSaving || !gtForm?.cc_ndc.trim()}
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {gtSaving ? 'Saving…' : gtForm?.editing ? 'Save' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => setGtForm(null)}
              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This edits the shared operator catalogue, so it applies to every brand — not
            only {selectedBrand?.name}. It also saves immediately.
          </div>

          <div>
            <label htmlFor="gt_cc_ndc" className="block text-sm font-medium text-gray-700">
              CC+NDC <span className="text-red-500">*</span>
            </label>
            <input
              id="gt_cc_ndc"
              type="text"
              inputMode="numeric"
              value={gtForm?.cc_ndc ?? ''}
              onChange={(e) => gtForm && setGtForm({ ...gtForm, cc_ndc: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleGtSave()}
              placeholder="38760"
              autoFocus
              className="mt-1 block w-full rounded-md border-gray-300 font-mono shadow-sm sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              The leading digits of the visited VLR&rsquo;s Global Title. Matched longest-prefix
              first, so a longer prefix wins over a shorter one. Digits only — stored as text,
              because leading zeros matter.
            </p>
          </div>

          <div>
            <label htmlFor="gt_node" className="block text-sm font-medium text-gray-700">GT node</label>
            <input
              id="gt_node"
              type="text"
              value={gtForm?.gt_node ?? ''}
              onChange={(e) => gtForm && setGtForm({ ...gtForm, gt_node: e.target.value })}
              placeholder="optional"
              className="mt-1 block w-full rounded-md border-gray-300 font-mono shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="gt_mgt" className="block text-sm font-medium text-gray-700">MGT</label>
            <input
              id="gt_mgt"
              type="text"
              value={gtForm?.mgt ?? ''}
              onChange={(e) => gtForm && setGtForm({ ...gtForm, mgt: e.target.value })}
              placeholder="optional"
              className="mt-1 block w-full rounded-md border-gray-300 font-mono shadow-sm sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Recorded for reference; neither field takes part in the steering decision.
            </p>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={gtDelete !== null}
        title="Delete GT prefix"
        confirmLabel="Delete"
        destructive
        busy={gtSaving}
        message={
          <>
            Delete GT prefix <strong>{gtDelete?.prefix.cc_ndc}</strong> from{' '}
            <strong>{gtDelete?.tadig}</strong>? HLR queries arriving on it will stop matching
            this network and be answered as an unknown network — for every brand, not only
            {' '}{selectedBrand?.name}.
          </>
        }
        onConfirm={handleGtDelete}
        onCancel={() => setGtDelete(null)}
      />
    </div>
  );
};

export default BrandNetworks;
